import { useQueryClient } from '@tanstack/react-query';
import { useAccount, useConnect, useDisconnect, useSignMessage } from 'wagmi';

import { Web3OtherConnectors, type Web3WagmiConnectors } from '@pkg/providers/Web3Provider/types';
import { getConnector } from '@pkg/providers/Web3Provider/utilities';

import { SupportedTokens } from '@app/client';
import {
    callAddWallet,
    callVerifyWallet,
    initializeWalletsAsync,
    useLegacyWalletStore,
    useRemoveWalletMutation,
} from '@app/store/zustand';

import { type AddressInfo, type SignaturePayload, type SignMessagePayload } from '../types';

export interface ConnectAnyWalletOptions {
    walletConnector: Web3WagmiConnectors | Web3OtherConnectors;
    onConnect?: () => void;
    onError?: () => void;
}

export const PETRA_INSTALL_URL = 'https://petra.app/';

//TODO: The functions below can probably be moved to /utilities
export function walletAddressWithPrefix(prefix: SupportedTokens, address = '') {
    return address.startsWith(prefix) ? prefix : `${prefix}.${address}`;
}

export function useConnectAnyWallet({ walletConnector, onConnect, onError }: ConnectAnyWalletOptions) {
    const queryClient = useQueryClient();

    const { isConnected } = useAccount();
    const { connectAsync: walletConnect, connectors } = useConnect();
    const { disconnectAsync } = useDisconnect();
    const { mutateAsync: handleRemoveWallet } = useRemoveWalletMutation();
    const { setConnecting } = useLegacyWalletStore();
    const { signMessageAsync } = useSignMessage();

    // TODO: Can this be moved into `useAddWalletMutation()`?
    async function addWalletMutation(incomingAddress: string, publicKey: string, currency: SupportedTokens) {
        const walletAddress = walletAddressWithPrefix(currency, incomingAddress);
        const cache = ['add-wallet', walletAddress];

        return queryClient.fetchQuery(
            cache,
            ({ signal }) =>
                callAddWallet({
                    variables: {
                        accountPublicKey: publicKey,
                        accountAddress: walletAddress,
                    },
                    settings: { signal },
                }),
            {
                retry: false,
            },
        );
    }

    // TODO: Can this be moved into `useVerifyWalletMutation()`?
    async function verifyWalletMutation(publicKey: string, signature: SignaturePayload) {
        const cache = ['verify-wallet', publicKey];

        return queryClient.fetchQuery(
            cache,
            ({ signal }) =>
                callVerifyWallet({
                    variables: {
                        publicKey: publicKey,
                        signature,
                    },
                    settings: { signal },
                }),
            {
                retry: false,
            },
        );
    }

    async function connectWagmi() {
        try {
            await disconnectAsync();
            const wallet = await walletConnect({
                connector: getConnector(connectors, walletConnector as Web3WagmiConnectors),
            });

            const currency = wallet.chain.id === 137 ? SupportedTokens.Polygon : SupportedTokens.Ethereum;

            const messageResponse = await addWalletMutation(wallet.account, wallet.account, currency);
            const signature = await signMessageAsync({ message: messageResponse.messageToSign.message });

            const isVerified = await verifyWalletMutation(wallet.account, { eip712: { signature } });

            if (isVerified) {
                onConnect?.();
            } else {
                onError?.();
            }
        } catch (error) {
            // TODO: We should pass `error` to `onError`.
            onError?.();
        }
    }

    function isPetraInstalled() {
        return 'aptos' in window;
    }

    function installPetra() {
        window.open(PETRA_INSTALL_URL, '_blank');
        return;
    }

    function getAptos() {
        if (isPetraInstalled()) {
            return window.aptos;
        } else {
            installPetra();
        }
    }

    async function signMessageWithPetra(payload: SignMessagePayload) {
        return window.aptos?.signMessage(payload);
    }

    async function connectPetra() {
        const aptos = getAptos();

        try {
            if (!aptos) {
                onError?.();
                return;
            }

            // TODO: Is `connect()` actually async?
            const wallet: AddressInfo = await aptos?.connect();

            const messageResponse = await addWalletMutation(wallet.address, wallet.publicKey, SupportedTokens.Aptos);
            const verifyAccount = await signMessageWithPetra(messageResponse.messageToSign as SignMessagePayload);

            const isVerified = await verifyWalletMutation(wallet.publicKey, { petra: verifyAccount });

            if (isVerified) {
                onConnect?.();
            } else {
                onError?.();
            }
        } catch (error) {
            // TODO: We should pass `error` to `onError`.
            onError?.();
        }
    }

    async function connect() {
        if (walletConnector === Web3OtherConnectors.Petra) {
            await connectPetra();
        } else {
            await connectWagmi();
        }
        void (await initializeWalletsAsync());
        setConnecting(undefined);
    }

    // TODO: This method is not implemented correctly!
    // Gabe will need to revisit it and help with the correct implementation.
    async function disconnect(accountAddress: string) {
        if (walletConnector === Web3OtherConnectors.Petra) {
            // TODO: Do we need to do anything special for Petra?
        } else {
            // TODO: How does this function even know what address to disconnect?
            await disconnectAsync();
        }

        await handleRemoveWallet({ accountAddress });
    }

    return {
        connect,
        disconnect,
        isConnected,
    };
}
